const sameId = (a, b) => String(a) === String(b)

/** Matches on product, so a list holding another size still reads as enabled. */
const matchingItems = (list, productId) =>
  (list.items ?? []).filter((item) => sameId(item?.meta?.productId, productId))

/**
 * Prefer the size being viewed. The bug this replaces matched on variant, so real lists
 * already hold two sizes of one product; taking the first match would show an arbitrary one.
 */
const findItem = (items, selectedVariantId) =>
  items.find((item) => sameId(item?.meta?.variantId, selectedVariantId)) ?? items[0]

export const seedDraft = ({ lists, productId, selectedVariantId }) => {
  const entries = {}
  lists.forEach((list) => {
    const items = matchingItems(list, productId)
    const item = findItem(items, selectedVariantId)
    entries[list.id] = item
      ? {
          enabled: true,
          variantId: item.meta.variantId,
          quantity: item.quantity ?? 1,
          itemId: item.id,
          // Every match, so disabling a row clears duplicates left by the old variant-matching bug.
          itemIds: items.map((match) => match.id)
        }
      : { enabled: false, variantId: selectedVariantId, quantity: 1, itemId: null, itemIds: [] }
  })

  const firstEnabled = lists.find((list) => entries[list.id].enabled)
  return { entries, activeListId: (firstEnabled ?? lists[0])?.id ?? null }
}

const patch = (draft, listId, changes) => ({
  ...draft,
  entries: { ...draft.entries, [listId]: { ...draft.entries[listId], ...changes } }
})

export const setActive = (draft, listId) => ({ ...draft, activeListId: listId })

export const toggleList = (draft, listId) => {
  const enabled = !draft.entries[listId].enabled
  const next = patch(draft, listId, { enabled })
  return enabled ? setActive(next, listId) : next
}

export const setSize = (draft, variantId) =>
  draft.activeListId == null ? draft : patch(draft, draft.activeListId, { variantId })

export const setQuantity = (draft, quantity) =>
  draft.activeListId == null ? draft : patch(draft, draft.activeListId, { quantity })

export const diffDraft = (seeded, current, { lists, productId, skuFor, img }) => {
  // Every consumer reads all four: AccountWishlist hides an item with no `selected`,
  // and the thumbnails everywhere (including this drawer's own rows) read `img`.
  const metaFor = (variantId) => ({ productId, variantId, selected: skuFor(variantId), img })

  const operations = []

  lists.forEach((list) => {
    const before = seeded.entries[list.id]
    const after = current.entries[list.id]
    if (!before || !after) return

    const connectionId = list.profile?.id ?? null
    const base = { listId: list.id, connectionId }

    if (!before.enabled && after.enabled) {
      operations.push({ ...base, type: 'add', sku: skuFor(after.variantId), quantity: after.quantity, meta: metaFor(after.variantId) })
      return
    }
    if (before.enabled && !after.enabled) {
      const ids = before.itemIds?.length ? before.itemIds : [before.itemId]
      ids.forEach((itemId) => operations.push({ ...base, type: 'remove', itemId }))
      return
    }
    if (!before.enabled) return

    if (!sameId(before.variantId, after.variantId)) {
      // Omneo keys an item by product_variant_sku, so a size change is a replace.
      operations.push({ ...base, type: 'remove', itemId: before.itemId })
      operations.push({ ...base, type: 'add', sku: skuFor(after.variantId), quantity: after.quantity, meta: metaFor(after.variantId) })
      return
    }
    if (before.quantity !== after.quantity) {
      operations.push({ ...base, type: 'update', itemId: before.itemId, quantity: after.quantity })
    }
  })

  return operations
}

export const hasChanges = (seeded, current) =>
  Object.keys(current.entries).some((listId) => {
    const before = seeded.entries[listId]
    const after = current.entries[listId]
    if (!before) return true
    if (before.enabled !== after.enabled) return true
    if (!after.enabled) return false
    return !sameId(before.variantId, after.variantId) || before.quantity !== after.quantity
  })
